import type { BlockNode } from '@shared/block-renderer';

/**
 * The informational-page block set — **three kinds, and the pack fixes both the
 * list and its order**: عنوان · فقرة · قائمة.
 *
 * Three rather than the editorial body's seven because `[x]` §21.1 names no
 * media, no quote and no note for an informational page, and the public renderer
 * styles nothing else for that body. **A subset, not a second dialect** — the
 * hook vocabulary is the editorial editor's, transcribed rather than re-spelled.
 *
 * **The set is deliberately not written into the shared block registry.** That
 * registry backs the *flow* editor's slash menu, which is the editorial post's
 * surface; a `heading` definition pushed into it would put an informational-page
 * kind in a menu the pack does not draw it in. This surface is a field per
 * block over a closed three-member set, so it reads its own table.
 */

export const PAGE_BLOCK_TYPES = ['heading', 'paragraph', 'list'] as const;

export type PageBlockType = (typeof PAGE_BLOCK_TYPES)[number];

export interface PageBlockSpec {
  /** The badge on the block head, and the label on its add button. */
  label: string;
  /** A one-line control, or the multi-line one a body needs. */
  multiline: boolean;
  /** The visually-hidden label bound to the control by `for`. */
  fieldLabel: string;
  /** Rendered under the control where the kind needs a rule stated. */
  hint?: string;
}

export const PAGE_BLOCKS: Readonly<Record<PageBlockType, PageBlockSpec>> = {
  heading: { label: 'عنوان', multiline: false, fieldLabel: 'نصّ العنوان' },
  paragraph: { label: 'فقرة', multiline: true, fieldLabel: 'نصّ الفقرة' },
  list: {
    label: 'قائمة',
    multiline: true,
    fieldLabel: 'عناصر القائمة',
    hint: 'سطر لكلّ عنصر. السطر الفارغ يُهمَل.',
  },
};

export function isPageBlockType(value: string): value is PageBlockType {
  return value === 'heading' || value === 'paragraph' || value === 'list';
}

/**
 * The editable text of a block, in the one shape a `<textarea>` holds.
 *
 * A list stores its items as an array — that is what the renderer walks — and
 * edits as one line per item, which is what the pack draws and what an operator
 * can type into without a control per row.
 */
export function blockText(block: BlockNode): string {
  if (block.type === 'list') {
    return Array.isArray(block.data.items)
      ? block.data.items.filter((item): item is string => typeof item === 'string').join('\n')
      : '';
  }
  return typeof block.data.text === 'string' ? block.data.text : '';
}

/** The inverse: what the control holds, back into the node's own shape. */
export function withText(block: BlockNode, value: string): BlockNode {
  if (block.type === 'list') {
    // The empty line is dropped rather than stored, because the hint says so and
    // a stored empty item renders as an empty bullet on the public page.
    return { ...block, data: { items: value.split('\n').filter((line) => line.trim().length > 0) } };
  }
  return { ...block, data: { text: value } };
}

export function createPageBlock(type: PageBlockType, id: string): BlockNode {
  return type === 'list' ? { id, type, data: { items: [] } } : { id, type, data: { text: '' } };
}
